import { intervalToDuration } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { monthsNames } from './dashboard'
import { IAdmissionRowSlots, IFetchPregnancy, IHealthProcedures } from '~/interfaces'
import { message } from '~/services'

export const regex = {
  brazilianCellphone: /^\+?\d{2}?\s*\(\d{2}\)?\s*\d{4,5}-?\d{4}$/g,
  email: /^ *([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,6}) *$/,
  // https://newbedev.com/regex-to-validate-date-format-dd-mm-yyyy
  date: /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]|(?:Jan|Mar|May|Jul|Aug|Oct|Dec)))\1|(?:(?:29|30)(\/|-|\.)(?:0?[1,3-9]|1[0-2]|(?:Jan|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)(?:0?2|(?:Feb))\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9]|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep))|(?:1[0-2]|(?:Oct|Nov|Dec)))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})/i,
  cpf: /(\d{3}).(\d{3}).(\d{3})-(\d{2})/,
  onlyNumbers: /[^\d]+/g,
  noEmojis:
    /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g
}

export const masks = {
  brazilianCellphone: '99 9999-99999',
  brazilianCellphoneWithNine: '99 99999-9999'
}

export const capitalize = (value: string): string => {
  if (!value) return value
  const newValue = value.toLowerCase()
  return newValue
    .split(' ')
    .map(item => (item.length > 2 ? item.charAt(0).toUpperCase() + item.slice(1) : item))
    .join(' ')
}

export const capitalizeOnlyFirstWord = (word: string): string => {
  if (!word) return word
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export const hasObjectValue = (object: Record<string, unknown>): boolean => {
  return Object.keys(object).length !== 0
}

export const hasArrayItems = (array: unknown[]): boolean => {
  return array.length > 0
}

export const replaceStringDashesBy =
  (replacer: string) =>
  (value?: string): string =>
    value ? value.replaceAll(' - ', replacer) : ''

export const cpfFormatter = (cpf: string): string => {
  if (!cpf) return cpf

  const cpfValue = cpf.replace(regex.onlyNumbers, '')

  if (cpfValue.length !== 11) return cpf

  return cpfValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

export const phoneNumberFormatter = (phoneNumber?: string): string => {
  if (!phoneNumber) return ''
  phoneNumber = phoneNumber.replace(/\D/g, '')

  // tell
  if (phoneNumber.length === 10) return phoneNumber.replace(/(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3')

  // cel
  if (phoneNumber.length === 11) return phoneNumber.replace(/(\d{2})(\d)(\d{4})(\d{4})$/, '($1) $2$3-$4')

  return phoneNumber
}

// TODO: Aguardando padrão de telefone UX
export const changePhone = (value: string): string => {
  if (!value || value.length < 10) return ''
  const number = value.replace('-', '').split('')
  const ddd = `${number.shift()}${number.shift()}`
  number[number.length - 4] = `-${number[number.length - 4]}`
  return `${ddd} ${number.join('')}`
}

export const replaceAndTrimPhone = (value: string): string => {
  return value.trim().replace('-', '').replace('(', '').replace(')', '').replace('+', '').replace(' ', '')
}

export const stringToDate = (stringDate: string): Date => {
  const date = stringDate.split('/')
  return new Date(`${date[1]}/${date[0]}/${date[2]}`)
}

export const removeEmptyOrNullFromJson = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: Record<string, any> | Array<Record<string, any>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> | Array<Record<string, any>> | any => {
  if (Array.isArray(obj)) {
    return obj
      .map(v => (v && !(v instanceof Date) && typeof v === 'object' ? removeEmptyOrNullFromJson(v) : v))
      .filter(v => v)
  } else {
    return Object.entries(obj)
      .map(([k, v]) => [k, v && !(v instanceof Date) && typeof v === 'object' ? removeEmptyOrNullFromJson(v) : v])
      .reduce((a, [k, v]) => (typeof v !== 'boolean' && typeof v !== 'number' && !v ? a : ((a[k] = v), a)), {})
  }
}

export const getAgeByBirthDate = (date: string): number | undefined => {
  if (!date) return
  const today = new Date()
  const birthDate = new Date(date)
  let age = today.getFullYear() - birthDate.getFullYear()
  const month = today.getMonth() - birthDate.getMonth()
  if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

export const getIntervalBetweenCurrentDateAnd = (birthDate: Date): string => {
  const currentDate = Date.now()
  const { years, months, days } = intervalToDuration({ start: currentDate, end: birthDate })
  let intervalText = ''

  const hasYears = years && years > 0
  const hasMonths = months && months > 0
  const hasDays = days && days > 0

  if (hasYears) {
    const yearLabel = years === 1 ? 'ano' : 'anos'
    intervalText = `${years} ${yearLabel}`
  }

  if (hasYears && hasMonths) {
    intervalText = `${intervalText} e`
  }

  if (hasMonths) {
    const monthLabel = months === 1 ? 'mês' : 'meses'
    intervalText = `${intervalText} ${months} ${monthLabel}`
  }

  if (hasYears && hasMonths) {
    return intervalText
  }

  if (hasYears ?? hasMonths) {
    intervalText = `${intervalText} e`
  }

  if (hasDays) {
    const dayLabel = days === 1 ? 'dia' : 'dias'
    intervalText = `${intervalText} ${days} ${dayLabel}`
  }

  return intervalText
}

export const getMonthDiff = (startDate: number, endDate: number): number => {
  const msInDay = 30 * 24 * 60 * 60 * 1000
  return Number((Math.abs(endDate - startDate) / msInDay).toFixed(2))
}

export interface ITransformDate {
  date: Date | string
  withHourAndMinute?: boolean
  dateFormat?: string
}

export const handleValidateDays = (value: string): boolean | string => {
  if (parseInt(value) <= 0) {
    return message('forms.numberOfDaysError')
  }
  return true
}

export const safeParseNumber = (value: string | number | undefined): number | undefined => {
  return typeof value === 'string' ? parseInt(value) : value
}

export const transformDate = ({ date, withHourAndMinute = false, dateFormat }: ITransformDate): string => {
  if (!date) return ''

  let dateFormatter = dateFormat ?? 'dd/MM/yyyy'

  if (withHourAndMinute && !dateFormat) {
    dateFormatter = 'dd/MM/yyyy - HH:mm'
  }

  return formatInTimeZone(date, 'America/Sao_Paulo', dateFormatter)
}

export const resetTimePartOfDateToEndOfDay = (hourWithTime: string): string => {
  const aDate = new Date(hourWithTime)
  aDate.setUTCHours(23, 59, 59, 999)
  return aDate.toISOString()
}

export enum Gender {
  MALE = 'M',
  FEMALE = 'F'
}

export const getGreeting = (hour: number): string | undefined => {
  const noon = 12

  const hourIsInvalid = hour < 0 || hour > 23
  if (hourIsInvalid) return 'Olá'

  const isBeforeNoon = hour <= noon
  if (isBeforeNoon) return 'Bom dia'

  const isAfterNoonAndBeforeOrEqual18 = hour > noon && hour <= 18
  if (isAfterNoonAndBeforeOrEqual18) return 'Boa tarde'

  const isAfter18 = hour > 18
  if (isAfter18) return 'Boa noite'
}

export const formatAdmissionInProgressDescription = (admission: IAdmissionRowSlots[]): string => {
  return `${admission[0].description} - Em andamento`
}

export const createMultilineString = (strings: string[]): string => {
  const withoutDuplicates = Array.from(new Set<string>(strings))
  const multilineString = withoutDuplicates.join('\n')
  return multilineString
}

export const formatHealthProceduresTooltipDescriptionWithDate = (healthProcedures: IHealthProcedures[]): string[] => {
  return healthProcedures.map(
    healthProcedure =>
      `${healthProcedure.description} - ${transformDate({
        date: new Date(healthProcedure.startDate),
        dateFormat: 'dd/MM/yyyy'
      })}`
  )
}

export const preventTooltipOfBigDescriptionsIn = (descriptions: string[], maxLine = 5): string => {
  const quantityOfDescriptionsToSlice = maxLine

  if (descriptions.length > quantityOfDescriptionsToSlice) {
    const sliceDescriptions = descriptions.slice(0, quantityOfDescriptionsToSlice).join('\n')
    const descriptionNumber = descriptions.length - quantityOfDescriptionsToSlice
    return `${sliceDescriptions}\n+${descriptionNumber}`
  }

  return descriptions.join('\n')
}

export const formatMinutesInHours = (totalMinutes: number): string => {
  const minutes = totalMinutes % 60
  const hours = Math.floor(totalMinutes / 60)
  const textMinutes = minutes > 0 ? `${minutes.toString().padStart(2, '0')}min` : ''
  const textHours = hours > 0 ? ` ${hours.toString().padStart(2, '0')}h` : ''
  return `${textHours}${textMinutes}`
}

export const getTimestamp = (date: string | Date): number => {
  return new Date(date).getTime()
}

const formatDate = (date: string | null): string => {
  if (!date) {
    return ' - '
  }
  return transformDate({ date })
}

const createDppText = (dppByDum: string, dppByUsg: string): string => {
  let text = ''
  if (dppByDum) {
    text = `${transformDate({ date: dppByDum })} (DUM)`
  }
  if (dppByUsg) {
    text = `${text}\n${transformDate({ date: dppByUsg })} (USG)`
  }
  if (!dppByDum && !dppByUsg) {
    text = ' - '
  }
  return text
}

const formatPregnancyPeriod = (startDate: string, endDate: string): string => {
  const monthPregnancyStartDate = new Date(startDate).getMonth()
  const YearPregnancyStartDate = new Date(startDate).getFullYear()
  const monthPregnancyEndDate = new Date(endDate).getMonth()
  const YearPregnancyEndDate = new Date(endDate).getFullYear()
  return `${YearPregnancyStartDate} (${monthsNames[monthPregnancyStartDate]}) - ${YearPregnancyEndDate} (${monthsNames[monthPregnancyEndDate]})`
}
const formatPregnancyPeriodEnd = (endDate: string): string => {
  const monthPregnancyEndDate = new Date(endDate).getMonth()
  const YearPregnancyEndDate = new Date(endDate).getFullYear()
  return `${YearPregnancyEndDate} (${monthsNames[monthPregnancyEndDate]})`
}
const parsePregnancy = (pregnancy: IFetchPregnancy): IFetchPregnancy => {
  return {
    ...pregnancy,
    pregnancyPeriod: formatPregnancyPeriod(pregnancy.startDate, pregnancy.endDate),
    pregnancyPeriodEnd: formatPregnancyPeriodEnd(pregnancy.endDate),
    dum: formatDate(pregnancy.dum),
    dppByDum: formatDate(pregnancy.dppByDum),
    dppByUsg: formatDate(pregnancy.dppByUsg),
    dpp: createDppText(pregnancy.dppByDum, pregnancy.dppByUsg),
    dateOfDelivery: formatDate(pregnancy.dateOfDelivery),
    typeOfDelivery: pregnancy.typeOfDelivery || ' - '
  }
}

export const parseModalPregnancies = (pregnancies: IFetchPregnancy[] | null): IFetchPregnancy[] => {
  if (!pregnancies || pregnancies.length <= 0) return []
  const validPregnancies = pregnancies.filter(pregnancy => pregnancy.startDate !== '' && pregnancy.endDate !== '')
  const parsedPregnancies = validPregnancies.map(parsePregnancy)
  return parsedPregnancies
}

export const areTheseStringsEqual = (str1 = '', str2 = ''): boolean => {
  const blankSpace = ' '
  return str1?.toLocaleLowerCase().replace(/\s/g, blankSpace) === str2?.toLocaleLowerCase().replace(/\s/g, blankSpace)
}

export * from './dashboard'
export * from './fp'
export * from './laboratoryTest'
